/*
  <product-addon> — gedrag van snippets/product-addon.liquid.

  Met het productformulier van het thema voegt de gewone koopknop de add-on toe: het element
  antwoordt op `product-form:collect-lines` (assets/product-form-lines.js) met zijn regel zodra het
  aantal boven 0 staat. Na een geslaagde toevoeging gaat het aantal terug naar 0. Zonder
  productformulier, of met de instelling own_button, gebruikt het een eigen knop.
*/

if (!customElements.get('product-addon')) {
  const fill = (template, values) =>
    Object.entries(values).reduce((text, [token, value]) => text.split(`%%${token}%%`).join(value), template);

  class ProductAddon extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const json = this.querySelector('script[type="application/json"]');
      if (!json) return;
      this.ready = true;

      this.data = JSON.parse(json.textContent);
      this.priceEl = this.querySelector('[data-role="price"]');
      this.compareEl = this.querySelector('[data-role="price-compare"]');
      this.badgeEl = this.querySelector('[data-role="discount-badge"]');
      this.hintEl = this.querySelector('[data-role="hint"]');
      this.variantSelect = this.querySelector('[data-role="variant-select"]');
      this.checkbox = this.querySelector('[data-role="checkbox"]');
      // Checkbox-modus (optioneel extraatje zonder aantal, bijv. spoedlevering): start uit (0),
      // de klant vinkt aan. Stepper-modus (bestaand gedrag): start op 1, niet 0 — de klant hoeft
      // alleen op "Toevoegen" te drukken. Ondergrens blijft dan 1 (zie change()); de stepper
      // verwijdert de add-on niet, dat doet alleen de cart zelf.
      this.quantity = this.checkbox ? 0 : 1;
      this.quantityEl = this.querySelector('[data-role="quantity"]');
      this.minus = this.querySelector('[data-role="minus"]');
      this.addButton = this.querySelector('[data-role="add"]');
      this.status = this.querySelector('[data-role="status"]');

      this.priceEl.textContent = window.zawajMoney.format(this.data.price, this.data);

      // Meer dan 1 variant (bijv. Kleur x Formaat bij de Fotolijst): de klant kiest zelf, wij
      // sturen nooit zomaar "eerste beschikbare" mee. Bij precies 1 variant is er geen select
      // en blijft data.variantId de vaste waarde uit de JSON hierboven.
      if (this.variantSelect) {
        this.variantSelect.addEventListener('change', () => {
          const option = this.variantSelect.selectedOptions[0];
          const variant = this.data.variants.find((v) => String(v.id) === option.value);
          if (!variant) return;
          this.data.variantId = variant.id;
          this.applyPricing(variant);
        });
      }

      if (this.checkbox) {
        this.checkbox.addEventListener('change', () => {
          this.quantity = this.checkbox.checked ? 1 : 0;
        });
      } else {
        this.minus.addEventListener('click', () => this.change(-1));
        this.querySelector('[data-role="plus"]').addEventListener('click', () => this.change(1));
        this.addButton.addEventListener('click', () => this.addDirect());
      }

      // Met eigen knop (own_button) doet de koopknop niets met de add-on. Checkbox-modus stuurt
      // altijd mee met de koopknop (own_button staat daar altijd al uit, zie product-addon-row.liquid).
      if (!this.data.ownButton) {
        document.addEventListener('product-form:collect-lines', (event) => {
          const { detail } = event;
          if (this.quantity > 0 && this.pageSection.contains(detail.form)) {
            detail.addLine({ id: this.data.variantId, quantity: this.quantity, properties: {} });
          }
        });
      }

      // Pas na het laden: het productformulier en de thema-events staan verderop in de pagina.
      const whenReady = () => {
        const hasForm = this.pageSection.querySelector('product-form form[action*="/cart/add"]');
        if (hasForm && !this.data.ownButton && this.addButton) this.addButton.style.display = 'none';
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (!this.data.ownButton && event && event.source === 'product-form') {
              if (this.checkbox) {
                this.checkbox.checked = false;
                this.quantity = 0;
              } else {
                this.change(-this.quantity);
              }
            }
          });
        }
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', whenReady);
      else whenReady();

      this.render();
    }

    get pageSection() {
      return this.closest('[id^="shopify-section"]') || document;
    }

    change(step) {
      this.quantity = Math.max(1, this.quantity + step);
      this.render();
    }

    // Herhaalt de prijslogica van snippets/product-addon-row.liquid client-side, voor het geval
    // van variant naar variant een ander bedrag geldt (bijv. een eigen compare_at_price per
    // kleur). Bij de Fotolijst is dat nu niet zo -- alle 8 varianten kosten hetzelfde -- maar dit
    // blijft kloppen ook als dat ooit wel verschilt. Kortingspercentage-afronding: zie de
    // toelichting bij row_discount_pct in product-addon-row.liquid (ceil naar veelvoud van 5, met
    // 1 procentpunt speling zodat een percentage dat maar net over een trede heen zit -- zoals de
    // ezel op 20,1% -- niet meteen naar de volgende trede springt).
    applyPricing(variant) {
      let showPrice = variant.price;
      let showCompare = 0;
      let discountPct = 0;
      let bundleOnly = false;
      const ceilToFive = (pct) => Math.ceil((pct - 1) / 5) * 5;

      if (variant.compareAtPrice > variant.price) {
        showCompare = variant.compareAtPrice;
        discountPct = ceilToFive(((variant.compareAtPrice - variant.price) / variant.compareAtPrice) * 100);
      } else if (this.data.displayPrice > 0 && this.data.displayPrice < variant.price) {
        showCompare = variant.price;
        showPrice = this.data.displayPrice;
        discountPct = ceilToFive(((variant.price - this.data.displayPrice) / variant.price) * 100);
        bundleOnly = true;
      }

      this.data.price = showPrice;
      this.priceEl.textContent = window.zawajMoney.format(showPrice, this.data);

      if (this.compareEl) {
        this.compareEl.hidden = showCompare <= showPrice;
        this.compareEl.textContent = window.zawajMoney.format(showCompare, this.data);
      }
      if (this.badgeEl) {
        this.badgeEl.hidden = discountPct <= 0;
        this.badgeEl.textContent = `-${discountPct}%`;
      }
      if (this.hintEl) this.hintEl.hidden = !bundleOnly;
    }

    render() {
      if (this.checkbox) return;
      this.quantityEl.textContent = this.quantity;
      this.minus.disabled = this.quantity <= 1;
    }

    showStatus(message, isError) {
      this.status.hidden = false;
      this.status.classList.toggle('is-error', isError);
      this.status.textContent = message;
    }

    // Zonder productformulier op de pagina, of met own_button: zelf toevoegen.
    addDirect() {
      if (this.quantity < 1) return;
      const i18n = this.data.i18n;
      const addUrl = `${window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add'}.js`;

      // Zelfde patroon als het thema's eigen product-form.js: vraag de cart-drawer/notification-
      // secties mee in dezelfde /cart/add.js-aanroep, en laat die daarna zichzelf herrenderen. Zonder
      // dit werd de regel wél echt toegevoegd (te zien in de cart-API), maar bleef de lade op de oude
      // stand staan tot een andere actie 'm toevallig opnieuw rendert — zag eruit als "toevoegen werkt
      // niet" terwijl het alleen de weergave was die achterbleef.
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = { items: [{ id: this.data.variantId, quantity: this.quantity }] };
      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((result) => {
          if (result.status) {
            this.showStatus(fill(i18n.cartError, { MESSAGE: result.description || result.message || '' }), true);
            return;
          }
          this.showStatus(fill(i18n.cartSuccess, { COUNT: this.quantity }), false);
          this.change(-this.quantity);
          if (cart) {
            // Bij een lege mand staat `is-empty` op het <cart-drawer>-element zelf; renderContents
            // haalt dat niet weg (product-form.js doet dat los, zie .finally()). Zonder dit blijft
            // de lade wit/leeg terwijl het item er wel in zit.
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            cart.renderContents(result);
          } else if (window.zawajRefreshCartIcon) {
            window.zawajRefreshCartIcon();
          }
        })
        .catch((error) => this.showStatus(fill(i18n.cartErrorGeneric, { ERROR: error }), true));
    }
  }

  customElements.define('product-addon', ProductAddon);
}
